import { fabric } from "fabric";
import { v4 as uuid } from "uuid";

/**
 * Västtrafik departures widget: a Fabric group with named children so players can refill
 * the texts without rebuilding (public/departures-widget.js does that on screens).
 * Child names: dep-bg, dep-title, dep-badge-{i}, dep-line-{i}, dep-dest-{i}, dep-time-{i}.
 */
export const DEPARTURES_ELE_TYPE = "departures-widget";
const W = 640;
const HEADER_H = 76;
const ROW_H = 58;
const PAD = 20;

export function defaultDeparturesObj() {
  return { gid: "", stopName: "Välj hållplats", rows: 5, bg: "#0f172a", fg: "#ffffff", muted: "#94a3b8" };
}

export function departuresHeight(rows) {
  return HEADER_H + rows * ROW_H + PAD;
}

export function buildDeparturesObjects(obj) {
  const o = { ...defaultDeparturesObj(), ...(obj || {}) };
  const rows = Math.min(Math.max(parseInt(o.rows, 10) || 5, 1), 10);
  const H = departuresHeight(rows);
  const base = { selectable: false, evented: false, objectCaching: false };
  const objs = [
    new fabric.Rect({ ...base, name: "dep-bg", left: 0, top: 0, width: W, height: H, fill: o.bg, rx: 18, ry: 18 }),
    new fabric.Text(o.stopName || "Avgångar", {
      ...base, name: "dep-title", left: PAD, top: 22, fontSize: 30, fontWeight: "bold", fill: o.fg, fontFamily: "Arial",
    }),
  ];
  for (let i = 0; i < rows; i++) {
    const y = HEADER_H + i * ROW_H;
    objs.push(
      new fabric.Rect({ ...base, name: `dep-badge-${i}`, left: PAD, top: y + 6, width: 72, height: 44, fill: "#334155", rx: 10, ry: 10 }),
      new fabric.Text("–", {
        ...base, name: `dep-line-${i}`, left: PAD + 36, top: y + 28, originX: "center", originY: "center",
        fontSize: 24, fontWeight: "bold", fill: "#ffffff", fontFamily: "Arial",
      }),
      new fabric.Text("", {
        ...base, name: `dep-dest-${i}`, left: PAD + 92, top: y + 28, originY: "center", fontSize: 26, fill: o.fg, fontFamily: "Arial",
      }),
      new fabric.Text("", {
        ...base, name: `dep-time-${i}`, left: W - PAD, top: y + 28, originX: "right", originY: "center",
        fontSize: 26, fontWeight: "bold", fill: o.fg, fontFamily: "Arial",
      })
    );
  }
  return objs;
}

export function createDeparturesGroup(obj, position = { left: 120, top: 120 }) {
  const data = { ...defaultDeparturesObj(), ...(obj || {}) };
  const group = new fabric.Group(buildDeparturesObjects(data), {
    left: position.left,
    top: position.top,
    scaleX: position.scaleX || 1,
    scaleY: position.scaleY || 1,
    subTargetCheck: false,
    hasBorders: true,
    hasControls: true,
    lockRotation: true,
  });
  group.id = position.id || uuid();
  group.eleType = DEPARTURES_ELE_TYPE;
  group.departuresObj = data;
  group.setCoords();
  return group;
}

/** "Nu", "4 min" or "14:05" — same rule as public/departures-widget.js. */
export function formatDepartureTime(dep, now = Date.now()) {
  if (dep.cancelled) return "Inställd";
  const t = Date.parse(dep.estimated || dep.planned || "");
  if (!Number.isFinite(t)) return "";
  const min = Math.floor((t - now) / 60000);
  if (min <= 0) return "Nu";
  if (min < 60) return `${min} min`;
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Fill a departures group with fresh data (editor preview). */
export function fillDeparturesGroup(group, data, now = Date.now()) {
  const subs = group._objects || [];
  const byName = (n) => subs.find((c) => c.name === n);
  const deps = (data && data.departures) || [];
  const o = group.departuresObj || defaultDeparturesObj();
  for (let i = 0; byName(`dep-dest-${i}`); i++) {
    const d = deps[i];
    byName(`dep-line-${i}`).set({ text: d ? d.line : "", fill: (d && d.lineFg) || "#ffffff" });
    byName(`dep-badge-${i}`).set({ fill: d ? d.lineBg || "#334155" : "transparent" });
    byName(`dep-dest-${i}`).set({ text: d ? d.direction : i === 0 ? "Inga avgångar" : "", fill: o.fg });
    byName(`dep-time-${i}`).set({ text: d ? formatDepartureTime(d, now) : "", fill: d && d.cancelled ? "#f87171" : o.fg });
  }
  group.dirty = true;
}
